/* eslint-disable max-lines */
import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { TransitionProps } from 'react-transition-group/Transition';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderWidthProps,
  DisplayProps,
  LeftProps,
  OverflowProps,
  PositionProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  borderRadius,
  display,
  left,
  overflow,
  position,
  space,
  themeGet,
  width,
  zIndex,
} from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import { focusOutlineCSS, outlineCSSWithOffset } from '../../theme/styles';
import type { SpaceProps } from '../../theme/system-types';
import type {
  CursorProps,
  PointerEventProps,
  ThemeBottomProps,
  ThemeHeightProps,
  ThemeRightProps,
  ThemeWidthProps,
  TransformProps,
} from '../../theme/system-utilities';
import {
  cursor,
  pointerEvents,
  scale,
  themeBottom,
  themeHeight,
  themeWidth,
  transform,
  transition,
} from '../../theme/system-utilities';
import { useCarouselKeyboardNavigation } from './hooks/use-carousel-keyboard';

interface CarouselPaginationProps {
  className?: string;
  goToSlide: (idx: number) => void;
  slideCount: number;
  currentSlide: number;
  paginationStyles?: ComponentProps<typeof Container>;
  nextSlide: () => void;
  previousSlide: () => void;
  baseId?: string;
  disablePaginationClick?: boolean;
}

/** @todo: In case we are going to replace the defaultBottomCenterControls with this custom one, we need to decide if we want to keep the default perTouch value or change it to 1.
 * In case we want to keep the default perTouch value (unlimited by default), we need to replace SCROLL_STEP_TABS accordingly.
 */

const MAX_VISIBLE_TABS = 5;
const TAB_TRANSLATE_STEP = 12; // px per tab movement
const SCROLL_STEP_TABS = 1;
const TABLIST_WIDTH = '70px';
const SMALL_TAB_STYLE = {
  scale: '0.7',
  transition: 'transform 0.2s ease',
};

const PaginationTrack = styled.div<
  DisplayProps & WidthProps & SpaceProps & Partial<TransitionProps>
>`
  ${display};
  ${width};
  ${space};
  ${transition};
  list-style: none;
  transform: translateX(${({ translateX }) => translateX}px);
`;

const paginationTrackStyles: ComponentProps<typeof PaginationTrack> = {
  display: 'flex',
  width: TABLIST_WIDTH,
  m: 'sp_0',
  p: 'sp_0',
  transition: 'transform 0.3s ease',
};

const DotButton = styled.button<
  BackgroundColorProps &
    BorderProps &
    BorderRadiusProps &
    BorderWidthProps &
    DisplayProps &
    PointerEventProps &
    SpaceProps &
    ThemeHeightProps &
    ThemeRightProps &
    ThemeWidthProps &
    CursorProps
>`
  ${display};
  ${space};
  ${border};
  ${borderRadius};
  ${themeWidth};
  ${themeHeight};
  ${backgroundColor};
  ${pointerEvents};
  ${scale};
  ${transition};
  ${cursor};

  padding-left: 0px !important;
  -webkit-appearance: none;

  &:focus {
    outline: none;
  }

  &[aria-selected='true'] {
    span {
      background-color: ${themeGet('colors.sys.neutral.background.strong')};
    }
    ${focusOutlineCSS};
  }

  &[aria-hidden='true'] {
    span {
      background-color: ${themeGet('colors.sys.neutral.background.inverted')};
      transform: scale(0.5);
    }
  }
`;

const dotButtonStyles: ComponentProps<typeof DotButton> = {
  display: 'block',
  themeWidth: 'sp_8',
  themeHeight: 'sp_8',
  mx: 'sp_2',
  m: 'sp_0',
  p: 'sp_0',
  border: 'none',
  borderRadius: 'circle',
  backgroundColor: 'transparent',
  pointerEvents: ['none', null, 'auto'] as unknown as PointerEventProps['pointerEvents'],
  cursor: 'pointer',
};

const Container = styled.div<
  ThemeBottomProps &
    LeftProps &
    OverflowProps &
    PositionProps &
    SpaceProps &
    TransformProps &
    ZIndexProps
>`
  ${left};
  ${overflow};
  ${position};
  ${space};
  ${themeBottom};
  ${transform};
  ${zIndex};
  &:focus-within {
    ${outlineCSSWithOffset};
  }
`;

const containerStyles: ComponentProps<typeof Container> = {
  left: '50%',
  overflow: 'hidden',
  position: 'absolute',
  transform: 'translateX(-50%)',
  mt: ['sp_4', 'sp_8'],
  themeBottom: '0px',
  zIndex: 1,
};

const Dot = styled.span<
  BackgroundColorProps &
    BorderRadiusProps &
    DisplayProps &
    SpaceProps &
    ThemeHeightProps &
    ThemeWidthProps &
    TransformProps
>`
  ${display};
  ${space};
  ${themeWidth};
  ${themeHeight};
  ${backgroundColor};
  ${borderRadius};
  ${scale};
  ${transition};
`;

const dotStyles: ComponentProps<typeof Dot> = {
  display: 'block',
  themeWidth: 'sp_8',
  themeHeight: 'sp_8',
  m: 'sp_0',
  p: 'sp_0',
  borderRadius: 'circle',
  backgroundColor: 'sys.neutral.background.inverted',
};

const getFirstVisibleTabIndex = (currentIndex: number) =>
  currentIndex + SCROLL_STEP_TABS - MAX_VISIBLE_TABS;

const useTranslateXOffset = (currentIndex: number) => {
  const [translateX, setTranslateX] = useState(TAB_TRANSLATE_STEP);

  const updateTranslateX = useCallback(
    (movedBack?: boolean) => {
      setTranslateX(prevTranslateX => {
        if (currentIndex === 0) {
          return TAB_TRANSLATE_STEP;
        }

        if (movedBack) {
          return prevTranslateX + TAB_TRANSLATE_STEP;
        }

        return -(getFirstVisibleTabIndex(currentIndex) * TAB_TRANSLATE_STEP);
      });
    },
    [currentIndex]
  );

  return { translateX, updateTranslateX };
};

const INITIAL_TAB_RANGE = { min: -1, max: MAX_VISIBLE_TABS - 1 };

const useVisibleTabs = (currentIndex: number, totalTabs: number) => {
  const [visibleRange, setVisibleRange] = useState(INITIAL_TAB_RANGE);
  const { translateX, updateTranslateX } = useTranslateXOffset(currentIndex);
  useEffect(() => {
    if (currentIndex >= visibleRange.max) {
      updateTranslateX();
      const isLastTab = currentIndex === totalTabs - 1;
      setVisibleRange({
        min: isLastTab ? totalTabs - MAX_VISIBLE_TABS : visibleRange.min + SCROLL_STEP_TABS,
        max: currentIndex + SCROLL_STEP_TABS,
      });
    }
    if (currentIndex <= visibleRange.min) {
      updateTranslateX(true);
      setVisibleRange(
        currentIndex === 0
          ? INITIAL_TAB_RANGE
          : {
              min: currentIndex - SCROLL_STEP_TABS,
              max: visibleRange.max - SCROLL_STEP_TABS,
            }
      );
    }
  }, [currentIndex, totalTabs, visibleRange, updateTranslateX]);

  return { visibleRange, translateX };
};

export const CarouselPagination = ({
  goToSlide,
  slideCount,
  currentSlide,
  paginationStyles,
  nextSlide,
  previousSlide,
  baseId = 'carousel',
  disablePaginationClick = true,
}: CarouselPaginationProps) => {
  const { t } = useLocalization();
  const { visibleRange, translateX } = useVisibleTabs(currentSlide, slideCount);

  const activeTabRef = useRef<HTMLButtonElement | null>(null);
  const handleKeyDown = useCarouselKeyboardNavigation({
    nextSlide,
    previousSlide,
    totalSlides: slideCount,
    goToSlide,
    activeTabRef,
  });

  return (
    <Container {...containerStyles} {...paginationStyles}>
      <PaginationTrack
        role="tablist"
        aria-label={t('h24_carousel_slides')}
        data-testid="carousel-pagination"
        translateX={translateX}
        {...paginationTrackStyles}
      >
        {Array.from({ length: slideCount }, (_, index) => {
          const isHidden = index < visibleRange.min || index > visibleRange.max;
          const isDisabled = isHidden;
          const dotId = `${baseId}-dot-${index}`;

          return (
            <DotButton
              key={dotId}
              role="tab"
              id={dotId}
              aria-controls={`${baseId}-slide-${index}`}
              aria-label={`${t('h24_carousel_slide')} ${index + 1}`}
              aria-selected={currentSlide === index}
              aria-roledescription={t('h24_carousel_slide')}
              disabled={isDisabled}
              tabIndex={currentSlide === index ? 0 : -1}
              onClick={() => !disablePaginationClick && goToSlide(index)}
              onKeyDown={handleKeyDown}
              ref={currentSlide === index ? activeTabRef : undefined}
              {...dotButtonStyles}
              {...(disablePaginationClick && { cursor: 'default' })}
            >
              <Dot
                {...dotStyles}
                {...([visibleRange.min, visibleRange.max].includes(index) && SMALL_TAB_STYLE)}
              />
            </DotButton>
          );
        })}
      </PaginationTrack>
    </Container>
  );
};
